// Preloads the stock page a person is about to open from the search box.
//
// Most search picks open a stock page (`/s/<exchange>/<ticker>`, or the reader's preferred tab of
// it). Two things make that navigation slow: the page's code, and its `getServerSideProps` props
// (`_next/data`). Both can be fetched while the person is still choosing:
//
//  - code, once per session: when the box first gets focus, the overview's files go into the HTTP
//    cache (`prefetchPageFiles`: fetched, not run), in an idle callback, never in the focus handler;
//  - props, per candidate: the router's own data cache gets the candidate's props
//    (`preloadNextData`), so a click takes them from memory. Candidates are the top result once the
//    query has settled, and the row the person highlights with the keyboard or rests the pointer on.
//
// None of it may slow the search down, so:
//
//  - nothing is fetched for a query until the server has answered it (`resultsArrived`). Rows the local
//    worker painted first do not count, and a highlight that comes before the answer waits for it;
//  - "settled" means the server has answered AND no keystroke for SETTLE_MS;
//  - a keystroke cancels every candidate that has not started (timers), so typing never queues work;
//  - each fetch is `priority: "low"`;
//  - caps: MAX_SETTLED_PER_QUERY + MAX_HIGHLIGHT_PER_QUERY per query, MAX_PER_SESSION per page
//    load, and each stock once per NEXT_DATA_TTL_MS (the life of a preloaded entry);
//  - nothing at all on a data-saver or 2G connection, or with `localStorage["oc:search-preload"]`
//    set to "off" (kept so the search can be timed with and without it on one build).
//
// The class is plain TypeScript with its side effects injected, so the unit tests drive it with
// fake timers (e2e/unit/searchPreload.spec.ts). `createSearchPreloader` wires it to the browser.

import type { NextRouter } from "next/router";

import { scheduleIdle } from "@/lib/defer";
import { hasPreloadedNextData, NEXT_DATA_TTL_MS, prefetchPageFiles, preloadNextData } from "@/lib/next-data-preload";
import { connectionAllowsPreload, currentConnection } from "@/lib/tab-preload";

/** The stock overview's route key in `__BUILD_MANIFEST`. */
export const STOCK_OVERVIEW_ROUTE = "/s/[exchange]/[ticker]";

/** No keystroke for this long, with the server's answer in, and the top result is preloaded. */
export const SETTLE_MS = 300;
/** A highlighted row must stay highlighted this long, so arrowing or sweeping past rows preloads nothing. */
export const DWELL_MS = 100;
export const MAX_SETTLED_PER_QUERY = 1;
export const MAX_HIGHLIGHT_PER_QUERY = 2;
export const MAX_PER_SESSION = 20;

export const KILL_SWITCH_KEY = "oc:search-preload";

/** A stock page a search row opens. */
export interface StockTarget {
    /** The path the navigation pushes, which is the data cache key. */
    asPath: string;
    /** The page's route key, e.g. `/s/[exchange]/[ticker]` or `/s/[exchange]/[ticker]/short-interest`. */
    route: string;
    /** The route's dynamic params, decoded, in route order (what the router passes to getDataHref). */
    query: { exchange: string; ticker: string };
    /** Dedupe key: exchange + ticker (+ tab), case-insensitive. */
    key: string;
}

const STOCK_PATH = /^\/s\/([^/?#]+)\/([^/?#]+)(?:\/([a-z0-9-]+))?\/?$/;

const decode = (segment: string): string | null => {
    try {
        return decodeURIComponent(segment);
    } catch {
        return null;
    }
};

/**
 * The stock page `path` opens, or null when it is not one we can preload: not a stock page, a page
 * nested deeper than one tab, a tab the build does not have, a query string, or a blank exchange
 * (the legacy dropdown links `/s/ ‎/<ticker>` for a row without one).
 *
 * `hasRoute` says whether the build has a route key; the overview is assumed to exist.
 */
export const stockTargetFor = (path: string | null | undefined, hasRoute: (route: string) => boolean): StockTarget | null => {
    if (!path || path.includes("?") || path.includes("#")) return null;
    const match = STOCK_PATH.exec(path);
    if (!match) return null;
    const exchange = decode(match[1]);
    const ticker = decode(match[2]);
    // \p{Cf}: format characters such as the left-to-right mark in the legacy blank-exchange link.
    if (!exchange || !ticker || !exchange.replace(/[\s\p{Cf}]/gu, "") || !ticker.trim()) return null;
    const tab = match[3];
    const route = tab ? `${STOCK_OVERVIEW_ROUTE}/${tab}` : STOCK_OVERVIEW_ROUTE;
    if (tab && !hasRoute(route)) return null;
    return { asPath: path.replace(/\/$/, ""), route, query: { exchange, ticker }, key: `${exchange}:${ticker}${tab ? `/${tab}` : ""}`.toLowerCase() };
};

export type Cancel = () => void;

export interface PreloaderDeps {
    /** Fetch the target's props into the router's data cache. Resolves true when stored. Never rejects. */
    preloadData: (target: StockTarget) => Promise<boolean>;
    /**
     * Whether the router still holds the target's preloaded props. A navigation consumes the entry,
     * so a stock opened from search is preloaded again the next time it is a candidate.
     */
    isCached: (target: StockTarget) => boolean;
    /** Prefetch a route's files (fetched, not run). */
    prefetchCode: (route: string) => void;
    /** Run `fn` when the browser is idle. */
    idle: (fn: () => void) => Cancel;
    /** Run `fn` after `ms`. */
    timer: (fn: () => void, ms: number) => Cancel;
    /** False on data saver / 2G, or when switched off. Read on every candidate. */
    allowed: () => boolean;
    now: () => number;
}

/** Why a candidate did or did not start; returned for tests and debugging. */
export type Outcome = "started" | "off" | "no-target" | "duplicate" | "query-cap" | "session-cap";

export class SearchPreloader {
    private query = "";
    private typedAt = 0;
    /** The query the server has answered, if it is the current one. */
    private answeredQuery: string | null = null;
    private topTarget: StockTarget | null = null;
    private highlighted: StockTarget | null = null;
    private settledCount = 0;
    private highlightCount = 0;
    private sessionCount = 0;
    /** Key -> when its preload started, and whether it is still in flight. */
    private readonly started = new Map<string, { at: number; inflight: boolean }>();
    private settleCancel: Cancel | null = null;
    private dwellCancel: Cancel | null = null;
    private codeRequested = false;
    private codeCancel: Cancel | null = null;
    /** Every candidate that started, in order (for tests and debugging). */
    readonly log: { key: string; reason: "settled" | "highlight"; query: string }[] = [];

    constructor(private readonly deps: PreloaderDeps) {}

    /** The search box got focus (or opened). Prefetches the overview's code once per session, when idle. */
    focus(): void {
        if (this.codeRequested || !this.deps.allowed()) return;
        this.codeRequested = true;
        this.codeCancel = this.deps.idle(() => {
            this.codeCancel = null;
            try {
                this.deps.prefetchCode(STOCK_OVERVIEW_ROUTE);
            } catch {
                /* a prefetch is only a head start */
            }
        });
    }

    /** The box now holds `query` (call from onChange, also with ""). Cancels every candidate not yet started. */
    typed(query: string): void {
        this.typedAt = this.deps.now();
        if (query === this.query) return;
        this.query = query;
        this.answeredQuery = null;
        this.topTarget = null;
        this.highlighted = null;
        this.settledCount = 0;
        this.highlightCount = 0;
        this.cancelPending();
    }

    /**
     * The server's answer for `query` is on screen; `top` is where its first row opens. Starts the
     * settle wait (the rest of SETTLE_MS since the last keystroke), and a highlight that was waiting.
     */
    resultsArrived(query: string, top: StockTarget | null): void {
        if (query !== this.query || !query.trim()) return;
        this.answeredQuery = query;
        this.topTarget = top;
        this.settleCancel?.();
        this.settleCancel = null;
        if (top) {
            const wait = Math.max(0, this.typedAt + SETTLE_MS - this.deps.now());
            this.settleCancel = this.deps.timer(() => {
                this.settleCancel = null;
                if (this.answeredQuery === query && this.topTarget === top) this.attempt(top, "settled");
            }, wait);
        }
        if (this.highlighted && !this.dwellCancel) this.armDwell(this.highlighted);
    }

    /** A row is highlighted (keyboard) or under the pointer; null when nothing is. */
    highlight(target: StockTarget | null): void {
        if (target?.key === this.highlighted?.key && (this.dwellCancel || !target)) return;
        this.highlighted = target;
        this.dwellCancel?.();
        this.dwellCancel = null;
        // Before the server has answered, the highlight waits (resultsArrived arms it).
        if (target && this.answeredQuery === this.query && this.query.trim()) this.armDwell(target);
    }

    /** The box closed: drop everything not yet started. Started preloads finish and stay cached. */
    end(): void {
        this.query = "";
        this.answeredQuery = null;
        this.topTarget = null;
        this.highlighted = null;
        this.cancelPending();
    }

    /** Unmount. */
    dispose(): void {
        this.end();
        this.codeCancel?.();
        this.codeCancel = null;
    }

    private armDwell(target: StockTarget): void {
        const query = this.query;
        this.dwellCancel = this.deps.timer(() => {
            this.dwellCancel = null;
            if (this.query === query && this.answeredQuery === query && this.highlighted === target) this.attempt(target, "highlight");
        }, DWELL_MS);
    }

    private cancelPending(): void {
        this.settleCancel?.();
        this.settleCancel = null;
        this.dwellCancel?.();
        this.dwellCancel = null;
    }

    private attempt(target: StockTarget, reason: "settled" | "highlight"): Outcome {
        if (!this.deps.allowed()) return "off";
        const now = this.deps.now();
        // In flight, or stored and still in the router's cache: nothing to do. A preload that
        // failed (or whose entry a navigation has used) does not block the next one.
        const seen = this.started.get(target.key);
        if (seen && (seen.inflight || (now - seen.at < NEXT_DATA_TTL_MS && this.deps.isCached(target)))) return "duplicate";
        if (reason === "settled" ? this.settledCount >= MAX_SETTLED_PER_QUERY : this.highlightCount >= MAX_HIGHLIGHT_PER_QUERY) return "query-cap";
        if (this.sessionCount >= MAX_PER_SESSION) return "session-cap";
        if (reason === "settled") this.settledCount += 1;
        else this.highlightCount += 1;
        this.sessionCount += 1;
        const entry = { at: now, inflight: true };
        this.started.set(target.key, entry);
        this.log.push({ key: target.key, reason, query: this.query });
        try {
            this.deps.prefetchCode(target.route);
        } catch {
            /* ignore */
        }
        // The session slot stays used either way, so a stock that never preloads cannot retry without bound.
        void this.deps
            .preloadData(target)
            .catch(() => false)
            .then((stored) => {
                entry.inflight = false;
                if (!stored && this.started.get(target.key) === entry) this.started.delete(target.key);
            });
        return "started";
    }
}

// ---- browser wiring ---------------------------------------------------------------------------

const killSwitchOn = (): boolean => {
    try {
        return window.localStorage.getItem(KILL_SWITCH_KEY) === "off";
    } catch {
        return false;
    }
};

const hasManifestRoute = (route: string): boolean => {
    try {
        const manifest = (window as unknown as { __BUILD_MANIFEST?: Record<string, unknown> }).__BUILD_MANIFEST;
        return Array.isArray(manifest?.[route]);
    } catch {
        return false;
    }
};

/** The target for a path in this build (tabs only when the build has that route). */
export const browserStockTarget = (path: string | null | undefined): StockTarget | null => (typeof window === "undefined" ? null : stockTargetFor(path, hasManifestRoute));

/** A preloader for one search box, wired to the browser and the pages router. */
export const createSearchPreloader = (router: NextRouter): SearchPreloader => {
    const off = typeof window === "undefined" || killSwitchOn();
    return new SearchPreloader({
        preloadData: (target) => preloadNextData(router, target.route, target.query, target.asPath, { priority: "low" }),
        isCached: (target) => hasPreloadedNextData(router, target.route, target.query, target.asPath),
        prefetchCode: (route) => {
            prefetchPageFiles(route);
        },
        idle: (fn) => scheduleIdle(fn),
        timer: (fn, ms) => {
            const handle = setTimeout(fn, ms);
            return () => clearTimeout(handle);
        },
        allowed: () => !off && connectionAllowsPreload(currentConnection()),
        now: () => Date.now(),
    });
};
